import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, PencilSimple, Trash, Eye, EyeSlash, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Admin CRUD for blog posts. Admin-only route.
 * - List all posts (incl. unpublished — uses sitemap endpoint that excludes them
 *   so we hit the bare collection via /api/blog?include_drafts=1; for simplicity
 *   here we just use the public list which already shows everything we publish).
 * - Editor: markdown textarea + cover image URL + tags.
 */
function emptyDraft() {
  return {
    slug: "",
    title: "",
    excerpt: "",
    content_markdown: "",
    cover_image: "",
    tags: "",
    published: true,
  };
}

export default function AdminBlog() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // existing post or "new"
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/blog");
      setPosts(r.data?.posts || []);
    } catch (e) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-app text-primary-fg flex items-center justify-center">
        <div className="text-sm text-muted-fg">Admin only.</div>
      </div>
    );
  }

  const startEdit = async (slug) => {
    try {
      const r = await api.get(`/blog/${slug}`);
      setEditing(slug);
      setDraft({
        slug: r.data.slug,
        title: r.data.title,
        excerpt: r.data.excerpt,
        content_markdown: r.data.content_markdown,
        cover_image: r.data.cover_image || "",
        tags: (r.data.tags || []).join(", "),
        published: r.data.published,
      });
    } catch {
      toast.error("Failed to load post");
    }
  };

  const startNew = () => {
    setEditing("new");
    setDraft(emptyDraft());
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(emptyDraft());
  };

  const save = async () => {
    if (!draft.title || draft.title.length < 4) return toast.error("Title required (min 4 chars)");
    if (!draft.excerpt || draft.excerpt.length < 10) return toast.error("Excerpt required (min 10 chars)");
    if (!draft.content_markdown || draft.content_markdown.length < 50) return toast.error("Content needs at least 50 chars");
    setSaving(true);
    try {
      const payload = {
        title: draft.title,
        excerpt: draft.excerpt,
        content_markdown: draft.content_markdown,
        cover_image: draft.cover_image || null,
        tags: draft.tags.split(",").map(s => s.trim()).filter(Boolean),
        published: draft.published,
        slug: draft.slug || undefined,
      };
      if (editing === "new") {
        await api.post("/blog", payload);
        toast.success("Post created");
      } else {
        await api.put(`/blog/${editing}`, payload);
        toast.success("Post updated");
      }
      await refresh();
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/blog/${slug}`);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-5"
        >
          <ArrowLeft size={11} weight="bold" />
          Back to Admin
        </Link>

        <div className="flex items-center justify-between mb-7">
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight">Blog · Admin</h1>
          {!editing && (
            <button
              type="button"
              onClick={startNew}
              data-testid="admin-blog-new"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium"
              style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
            >
              <Plus size={12} weight="bold" />
              New Post
            </button>
          )}
        </div>

        {editing ? (
          <div className="border border-subtle rounded-sm p-5 bg-surface/40 space-y-4" data-testid="admin-blog-editor">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Title</label>
                <input
                  value={draft.title}
                  onChange={e => setDraft({ ...draft, title: e.target.value })}
                  className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm"
                  data-testid="admin-blog-title"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
                  Slug (optional — auto from title)
                </label>
                <input
                  value={draft.slug}
                  onChange={e => setDraft({ ...draft, slug: e.target.value })}
                  placeholder="my-post-slug"
                  className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm font-mono"
                  data-testid="admin-blog-slug"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
                Excerpt (shown in list + meta description, max 400 chars)
              </label>
              <textarea
                value={draft.excerpt}
                onChange={e => setDraft({ ...draft, excerpt: e.target.value })}
                rows={3}
                className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm"
                data-testid="admin-blog-excerpt"
              />
            </div>

            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
                Content (Markdown — # headings, **bold**, lists, [links](/), ```code```)
              </label>
              <textarea
                value={draft.content_markdown}
                onChange={e => setDraft({ ...draft, content_markdown: e.target.value })}
                rows={20}
                className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm font-mono leading-relaxed"
                data-testid="admin-blog-content"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Cover Image URL (optional)</label>
                <input
                  value={draft.cover_image}
                  onChange={e => setDraft({ ...draft, cover_image: e.target.value })}
                  placeholder="https://…"
                  className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Tags (comma separated)</label>
                <input
                  value={draft.tags}
                  onChange={e => setDraft({ ...draft, tags: e.target.value })}
                  placeholder="education, breakout, nse"
                  className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm"
                />
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={draft.published}
                onChange={e => setDraft({ ...draft, published: e.target.checked })}
                data-testid="admin-blog-published"
              />
              Published (visible to public)
            </label>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="admin-blog-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-medium disabled:opacity-50"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
              >
                <Check size={12} weight="bold" />
                {saving ? "Saving…" : (editing === "new" ? "Create Post" : "Save Changes")}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-medium border border-subtle text-secondary-fg hover:bg-surface"
              >
                <X size={12} weight="bold" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="border border-subtle rounded-sm overflow-hidden" data-testid="admin-blog-list">
            {loading ? (
              <div className="p-5 text-xs font-mono text-muted-fg animate-pulse">/// Loading…</div>
            ) : posts.length === 0 ? (
              <div className="p-5 text-sm text-muted-fg">No posts yet — click <strong>New Post</strong>.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-subtle bg-surface/60">
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">Title</th>
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">Slug</th>
                    <th className="text-right px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">Views</th>
                    <th className="text-right px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">Status</th>
                    <th className="text-right px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.slug} className="border-b border-subtle/50 hover:bg-surface/40" data-testid={`admin-blog-row-${p.slug}`}>
                      <td className="px-4 py-3 text-primary-fg">{p.title}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-fg">{p.slug}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-secondary-fg">{p.views}</td>
                      <td className="px-4 py-3 text-right">
                        {p.published
                          ? <span className="text-pos text-[11px] font-mono inline-flex items-center gap-1"><Eye size={11}/> Live</span>
                          : <span className="text-muted-fg text-[11px] font-mono inline-flex items-center gap-1"><EyeSlash size={11}/> Draft</span>}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <button
                          type="button"
                          onClick={() => startEdit(p.slug)}
                          className="p-1.5 text-secondary-fg hover:text-pos rounded-sm hover:bg-surface"
                          data-testid={`admin-blog-edit-${p.slug}`}
                          aria-label="Edit"
                        >
                          <PencilSimple size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(p.slug)}
                          className="p-1.5 text-secondary-fg hover:text-neg rounded-sm hover:bg-surface"
                          data-testid={`admin-blog-delete-${p.slug}`}
                          aria-label="Delete"
                        >
                          <Trash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
